import type { Job } from "../../../../../../models";
import type { PropertyMappings } from "../../../../shared/PropertyMappings.types";
import type {
	WizardState,
	WizardAction,
	WizardStep,
	WizardPhase,
	WizardConfigurationState,
	OverlapIssue,
	DuplicateIssue,
	InternalDuplicateIssue,
	OverlappingZonesIssue,
	UnmatchedIssue,
	InvalidDataIssue,
	ConfigurationStep,
	ValidationStep,
} from "../WizardContainer";
import { CONFIGURATION_STEPS, VALIDATION_STEPS, WIZARD_STEPS } from "../WizardContainer/WizardContainer.types";

// ============================================================================
// Constants
// ============================================================================

// All steps in order (configuration then validation)
const STEP_ORDER: WizardStep[] = WIZARD_STEPS;

// ============================================================================
// Initial State Factory
// ============================================================================

/**
 * Creates an empty configuration state
 */
export function createEmptyConfigurationState(): WizardConfigurationState {
	return {
		warnings: [],
		uploadedFiles: [],
		geodataGroups: [],
		propertyMappings: {},
		geoTypeSelections: {},
		excludedGroupIds: new Set(),
		selectedGroupIdsForMap: [],
		geoDataMap: new Map(),
		selectableGeoData: {},
		originalFiles: [],
	};
}

/**
 * Creates the initial wizard state with the provided jobs (validation phase)
 */
export function createInitialWizardState(jobs: Job[]): WizardState {
	return {
		phase: "validation",
		configuration: createEmptyConfigurationState(),
		jobs,
		currentStep: "unmatched-features", // Start with unmatched (first validation step)
		currentIssueIndex: 0,
		issues: {
			overlaps: [],
			duplicateBoundaries: [],
			overlappingPoints: [],
			overlappingZones: [],
			unmatched: [],
			invalidData: [],
		},
		skippedIssueIds: new Set(),
		emptySteps: new Set(),
		completedSteps: new Set(),
		visitedSteps: new Set(["unmatched-features" as WizardStep]),
		stepsWithInitialIssues: new Set(),
		manualNavigation: false,
		isValidating: false,
		validationError: null,
		dealAssignments: {},
		branchAssignments: {},
		salespersonAssignments: {},
		hasBranches: false,
		isSalespersonRequired: false,
	};
}

/**
 * Creates the initial wizard state for configuration phase (with geodata)
 */
export function createInitialConfigurationState(configuration: WizardConfigurationState): WizardState {
	return {
		phase: "configuration",
		configuration,
		jobs: [],
		currentStep: "file-validation", // Start at first configuration step
		currentIssueIndex: 0,
		issues: {
			overlaps: [],
			duplicateBoundaries: [],
			overlappingPoints: [],
			overlappingZones: [],
			unmatched: [],
			invalidData: [],
		},
		skippedIssueIds: new Set(),
		emptySteps: new Set(),
		completedSteps: new Set(),
		visitedSteps: new Set(["file-validation" as WizardStep]),
		stepsWithInitialIssues: new Set(),
		manualNavigation: false,
		isValidating: false,
		validationError: null,
		dealAssignments: {},
		branchAssignments: {},
		salespersonAssignments: {},
		hasBranches: false,
		isSalespersonRequired: false,
	};
}

// ============================================================================
// Helper: Determine first step with issues from issues object
// ============================================================================

/**
 * Gets the first visible validation step, respecting the full step order.
 * This ensures always-visible steps (branch-configuration, deal-configuration,
 * salesperson-assignment) aren't skipped when only later issue-based steps have issues.
 */
function getFirstVisibleValidationStep(state: WizardState): WizardStep {
	for (const step of STEP_ORDER) {
		if (isConfigurationStep(step)) continue;
		if (shouldShowStep(state, step)) return step;
	}
	return "summary";
}

// ============================================================================
// Reducer
// ============================================================================

/**
 * Wizard state reducer - handles all state transitions
 */
export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
	switch (action.type) {
		// ============================================================================
		// Configuration Phase Actions
		// ============================================================================

		case "SET_CONFIGURATION":
			return {
				...state,
				phase: "configuration",
				configuration: action.configuration,
				currentStep: "file-validation",
				visitedSteps: new Set([...state.visitedSteps, "file-validation" as WizardStep]),
			};

		case "UPDATE_GEODATA":
			return {
				...state,
				configuration: {
					...state.configuration,
					...action.geodata,
				},
			};

		case "CLEAR_GEODATA":
			return {
				...state,
				// Reset to configuration phase
				phase: "configuration",
				// Clear jobs
				jobs: [],
				// Reset issues
				issues: {
					overlaps: [],
					duplicateBoundaries: [],
					overlappingPoints: [],
					overlappingZones: [],
					unmatched: [],
					invalidData: [],
				},
				// Clear skipped issues
				skippedIssueIds: new Set(),
				// Reset completed steps, visited steps, and initial issue tracking
				completedSteps: new Set(),
				visitedSteps: new Set(["file-validation" as WizardStep]),
				stepsWithInitialIssues: new Set(),
				// Reset issue index
				currentIssueIndex: 0,
				// Clear deal and branch assignments
				dealAssignments: {},
				branchAssignments: {},
				salespersonAssignments: {},
				// Clear configuration
				configuration: {
					...state.configuration,
					warnings: [],
					uploadedFiles: [],
					geodataGroups: [],
					propertyMappings: {},
					geoTypeSelections: {},
					excludedGroupIds: new Set(),
					selectedGroupIdsForMap: [],
					geoDataMap: new Map(),
					selectableGeoData: {},
					originalFiles: [],
				},
			};

		case "UPDATE_PROPERTY_MAPPINGS":
			return {
				...state,
				configuration: {
					...state.configuration,
					propertyMappings: {
						...state.configuration.propertyMappings,
						[action.groupId]: action.mappings,
					},
				},
			};

		case "UPDATE_ALL_PROPERTY_MAPPINGS": {
			return {
				...state,
				configuration: {
					...state.configuration,
					propertyMappings: action.mappings,
				},
			};
		}

		case "APPLY_GLOBAL_PROPERTY_MAPPING": {
			const newMappings = { ...state.configuration.propertyMappings };
			for (const group of state.configuration.geodataGroups) {
				if (!state.configuration.excludedGroupIds.has(group.id)) {
					newMappings[group.id] = {
						...(newMappings[group.id] || {}),
						[action.field]: action.value,
					} as PropertyMappings;
				}
			}
			return {
				...state,
				configuration: {
					...state.configuration,
					propertyMappings: newMappings,
				},
			};
		}

		case "UPDATE_GEO_TYPE":
			return {
				...state,
				configuration: {
					...state.configuration,
					geoTypeSelections: {
						...state.configuration.geoTypeSelections,
						[action.groupId]: action.geoType,
					},
				},
			};

		case "UPDATE_ALL_GEO_TYPES":
			return {
				...state,
				configuration: {
					...state.configuration,
					geoTypeSelections: {
						...state.configuration.geoTypeSelections,
						...action.updates,
					},
				},
			};

		case "EXCLUDE_GROUP": {
			const newExcluded = new Set(state.configuration.excludedGroupIds);
			newExcluded.add(action.groupId);
			// Also remove from map selection
			const newSelectedForMap = state.configuration.selectedGroupIdsForMap.filter((id) => id !== action.groupId);
			return {
				...state,
				configuration: {
					...state.configuration,
					excludedGroupIds: newExcluded,
					selectedGroupIdsForMap: newSelectedForMap,
				},
			};
		}

		case "INCLUDE_GROUP": {
			const newExcluded = new Set(state.configuration.excludedGroupIds);
			newExcluded.delete(action.groupId);
			return {
				...state,
				configuration: {
					...state.configuration,
					excludedGroupIds: newExcluded,
				},
			};
		}

		case "SET_SELECTED_GROUPS_FOR_MAP":
			return {
				...state,
				configuration: {
					...state.configuration,
					selectedGroupIdsForMap: action.groupIds,
				},
			};

		case "COMPLETE_CONFIGURATION": {
			// Transition from configuration to validation phase
			const newCompleted = new Set(state.completedSteps);
			// Mark all configuration steps as completed
			CONFIGURATION_STEPS.forEach((step) => newCompleted.add(step));

			const newVisited = new Set(state.visitedSteps);
			newVisited.add("unmatched-features" as WizardStep);

			return {
				...state,
				phase: "validation" as WizardPhase,
				jobs: action.jobs,
				currentStep: "unmatched-features" as WizardStep, // First validation step
				currentIssueIndex: 0,
				completedSteps: newCompleted,
				visitedSteps: newVisited,
				skippedIssueIds: new Set<string>(),
			};
		}

		// ============================================================================
		// Validation Phase Actions
		// ============================================================================

		case "SET_JOBS":
			return {
				...state,
				jobs: action.jobs,
				currentIssueIndex: 0,
				preserveStepOnNextValidation: action.preserveStep ?? false,
			};

		case "UPDATE_JOB": {
			const jobIndex = state.jobs.findIndex((j) => j.id === action.job.id);
			if (jobIndex === -1) return state;

			const newJobs = [...state.jobs];
			newJobs[jobIndex] = action.job;
			return {
				...state,
				jobs: newJobs,
				preserveStepOnNextValidation: true,
			};
		}

		case "DELETE_JOB":
			return {
				...state,
				jobs: state.jobs.filter((j) => j.id !== action.jobId),
				preserveStepOnNextValidation: true,
			};

		case "DELETE_JOBS": {
			const idsToDelete = new Set(action.jobIds);
			return {
				...state,
				jobs: state.jobs.filter((j) => !idsToDelete.has(j.id)),
				preserveStepOnNextValidation: true,
			};
		}

		case "SKIP_ISSUE": {
			const newSkipped = new Set(state.skippedIssueIds);
			newSkipped.add(action.issueId);
			return {
				...state,
				skippedIssueIds: newSkipped,
			};
		}

		case "UNSKIP_ISSUE": {
			const newSkipped = new Set(state.skippedIssueIds);
			newSkipped.delete(action.issueId);
			return {
				...state,
				skippedIssueIds: newSkipped,
			};
		}

		case "SKIP_ALL_STEP_ISSUES": {
			const issues = getIssuesForStep(state, state.currentStep);
			const newSkipped = new Set(state.skippedIssueIds);
			issues.forEach((issue) => newSkipped.add(issue.id));
			return {
				...state,
				skippedIssueIds: newSkipped,
			};
		}

		case "NEXT_STEP": {
			// Skip steps that have no issues (not visible in progress bar)
			const nextStep = getNextVisibleStep(state, state.currentStep);
			if (!nextStep) return state;

			const newCompleted = new Set(state.completedSteps);
			newCompleted.add(state.currentStep);

			const newVisitedNext = new Set(state.visitedSteps);
			newVisitedNext.add(nextStep);

			return {
				...state,
				currentStep: nextStep,
				currentIssueIndex: 0,
				completedSteps: newCompleted,
				visitedSteps: newVisitedNext,
				manualNavigation: false,
			};
		}

		case "PREV_STEP": {
			// Skip steps that have no issues (not visible in progress bar)
			const prevStep = getPreviousVisibleStep(state, state.currentStep);
			if (!prevStep) return state;

			// Get the issue count for the previous step to land on its last issue
			// This enables seamless backward navigation through issues
			const prevStepIssueCount = getStepIssueCount(state, prevStep);
			const lastIssueIndex = Math.max(0, prevStepIssueCount - 1);

			return {
				...state,
				currentStep: prevStep,
				currentIssueIndex: lastIssueIndex,
				manualNavigation: false,
			};
		}

		case "NEXT_ISSUE": {
			// Bounds check to prevent index from exceeding issue count
			const issues = getIssuesForStep(state, state.currentStep);
			const maxIndex = Math.max(0, issues.length - 1);
			return {
				...state,
				currentIssueIndex: Math.min(state.currentIssueIndex + 1, maxIndex),
			};
		}

		case "PREV_ISSUE":
			return {
				...state,
				currentIssueIndex: Math.max(0, state.currentIssueIndex - 1),
			};

		case "GO_TO_ISSUE":
			return {
				...state,
				currentIssueIndex: action.index,
			};

		case "GO_TO_STEP":
			return {
				...state,
				currentStep: action.step,
				currentIssueIndex: action.issueIndex ?? 0,
				visitedSteps: new Set([...state.visitedSteps, action.step]),
				manualNavigation: true,
			};

		case "SET_ISSUES": {
			// Determine which steps currently have issues
			const stepsWithIssuesNow = new Set<WizardStep>();
			if (action.issues.duplicateBoundaries.length > 0) stepsWithIssuesNow.add("duplicate-boundaries");
			if (action.issues.overlappingPoints.length > 0) stepsWithIssuesNow.add("overlapping-points");
			if (action.issues.overlaps.length > 0) stepsWithIssuesNow.add("overlapping-boundaries");
			if (action.issues.overlappingZones.length > 0) stepsWithIssuesNow.add("overlapping-zones");
			if (action.issues.unmatched.length > 0) stepsWithIssuesNow.add("unmatched-features");
			if (action.issues.invalidData.length > 0) stepsWithIssuesNow.add("missing-invalid-data");

			// Merge with existing stepsWithInitialIssues (union - append only)
			// Once a step has had issues, it stays in this set for the session
			const updatedInitialIssues = new Set([...state.stepsWithInitialIssues, ...stepsWithIssuesNow]);

			// If preserveStep is true OR if state has preserveStepOnNextValidation flag,
			// keep the current step. This is used when splitting jobs to avoid jumping back to earlier steps.
			const shouldPreserveStep = action.preserveStep || state.preserveStepOnNextValidation;
			if (shouldPreserveStep) {
				return {
					...state,
					issues: action.issues,
					stepsWithInitialIssues: updatedInitialIssues,
					preserveStepOnNextValidation: false, // Clear the flag
					manualNavigation: false, // Allow auto-advance after job changes
				};
			}
			// Otherwise, determine the first visible validation step
			// Build intermediate state so shouldShowStep() has access to updated issues
			const updatedState: WizardState = {
				...state,
				issues: action.issues,
				stepsWithInitialIssues: updatedInitialIssues,
				phase: "validation",
			};
			const firstStep = getFirstVisibleValidationStep(updatedState);
			return {
				...updatedState,
				currentStep: firstStep,
				currentIssueIndex: 0,
				visitedSteps: new Set([...state.visitedSteps, firstStep]),
				preserveStepOnNextValidation: false,
				manualNavigation: false, // Allow auto-advance after job changes
			};
		}

		case "SET_DEAL_ASSIGNMENTS":
			return {
				...state,
				dealAssignments: action.assignments,
			};

		case "SET_BRANCH_ASSIGNMENTS":
			return {
				...state,
				branchAssignments: action.assignments,
			};

		case "SET_SALESPERSON_ASSIGNMENTS":
			return {
				...state,
				salespersonAssignments: action.assignments,
			};

		case "SET_HAS_BRANCHES":
			return {
				...state,
				hasBranches: action.hasBranches,
			};

		case "SET_IS_SALESPERSON_REQUIRED":
			return {
				...state,
				isSalespersonRequired: action.isSalespersonRequired,
			};

		case "SET_VALIDATING":
			return {
				...state,
				isValidating: action.isValidating,
			};

		case "SET_VALIDATION_ERROR":
			return {
				...state,
				validationError: action.error,
			};

		case "MARK_STEP_COMPLETE": {
			const newCompleted = new Set(state.completedSteps);
			newCompleted.add(action.step);
			return {
				...state,
				completedSteps: newCompleted,
			};
		}

		case "RESET":
			return {
				...createInitialWizardState(state.jobs),
				jobs: state.jobs,
			};

		default:
			return state;
	}
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Gets the next step in the wizard sequence (without visibility check)
 */
export function getNextStep(currentStep: WizardStep): WizardStep | null {
	const currentIndex = STEP_ORDER.indexOf(currentStep);
	if (currentIndex === -1 || currentIndex >= STEP_ORDER.length - 1) {
		return null;
	}
	return STEP_ORDER[currentIndex + 1];
}

/**
 * Gets the next visible step (skips steps with no issues)
 */
export function getNextVisibleStep(state: WizardState, currentStep: WizardStep): WizardStep | null {
	const currentIndex = STEP_ORDER.indexOf(currentStep);
	if (currentIndex === -1) return null;

	for (let i = currentIndex + 1; i < STEP_ORDER.length; i++) {
		const step = STEP_ORDER[i];
		if (shouldShowStep(state, step)) {
			return step;
		}
	}
	return null;
}

/**
 * Gets the previous step in the wizard sequence (without visibility check)
 */
export function getPreviousStep(currentStep: WizardStep): WizardStep | null {
	const currentIndex = STEP_ORDER.indexOf(currentStep);
	if (currentIndex <= 0) {
		return null;
	}
	return STEP_ORDER[currentIndex - 1];
}

/**
 * Gets the previous visible step (skips steps with no issues)
 */
export function getPreviousVisibleStep(state: WizardState, currentStep: WizardStep): WizardStep | null {
	const currentIndex = STEP_ORDER.indexOf(currentStep);
	if (currentIndex <= 0) return null;

	for (let i = currentIndex - 1; i >= 0; i--) {
		const step = STEP_ORDER[i];
		if (shouldShowStep(state, step)) {
			return step;
		}
	}
	return null;
}

/**
 * Checks if the user can proceed from the current step
 * (all issues for the current step must be skipped, or configuration step requirements met)
 */
export function canProceedFromStep(state: WizardState): boolean {
	// Configuration step validation
	if (isConfigurationStep(state.currentStep)) {
		return canProceedFromConfigurationStep(state);
	}

	// Branch configuration step: all growers must be assigned to a branch
	if (state.currentStep === "branch-configuration") {
		return getUnassignedGrowerCountForBranches(state) === 0;
	}

	// Deal configuration step: all growers must be assigned to a deal
	if (state.currentStep === "deal-configuration") {
		return getUnassignedGrowerCount(state) === 0;
	}

	// Salesperson assignment step: required only when isSalespersonRequired is true
	if (state.currentStep === "salesperson-assignment") {
		return !state.isSalespersonRequired || getUnassignedGrowerCountForSalespersons(state) === 0;
	}

	// Validation step validation
	const issueCount = getStepIssueCount(state, state.currentStep);
	if (issueCount === 0) {
		return true;
	}

	const issues = getIssuesForStep(state, state.currentStep);

	// For missing-invalid-data step: never allow proceeding if any issue has a missing grower
	// (grower-missing issues cannot be skipped, so they must be fixed by editing the job)
	if (state.currentStep === "missing-invalid-data") {
		const hasGrowerMissingIssues = state.issues.invalidData.some((issue) => "grower" in issue.errors);
		if (hasGrowerMissingIssues) return false;
	}

	return issues.every((issue) => state.skippedIssueIds.has(issue.id));
}

/**
 * Checks if user can proceed from a configuration step
 */
export function canProceedFromConfigurationStep(state: WizardState): boolean {
	switch (state.currentStep) {
		case "file-validation":
			// Can always proceed from file validation (warnings are informational)
			return true;

		case "property-mapping":
			// Can always proceed from property mapping (mappings are optional)
			return true;

		case "geo-type-selection": {
			// Must have all non-excluded groups typed AND at least one boundary
			const { geodataGroups, geoTypeSelections, excludedGroupIds } = state.configuration;

			// Get non-excluded groups
			const activeGroups = geodataGroups.filter((g) => !excludedGroupIds.has(g.id));

			// All active groups must have a geo-type assigned
			const allTyped = activeGroups.every(
				(g) => geoTypeSelections[g.id] !== null && geoTypeSelections[g.id] !== undefined,
			);

			// At least one group must be assigned as Boundary
			const hasBoundary = activeGroups.some((g) => geoTypeSelections[g.id] === "Boundary");

			return allTyped && hasBoundary;
		}

		default:
			return true;
	}
}

/**
 * Gets the unique grower names from the jobs list
 */
export function getGrowerNames(state: WizardState): string[] {
	const names = new Set<string>();
	for (const job of state.jobs) {
		const growerName = job.importGrowerName || job.growerName || "";
		if (growerName) names.add(growerName);
	}
	return Array.from(names);
}

/**
 * Gets the count of growers that have not been assigned to a deal
 */
export function getUnassignedGrowerCount(state: WizardState): number {
	const growerNames = getGrowerNames(state);
	const assignments = state.dealAssignments || {};
	return growerNames.filter((name) => !assignments[name]).length;
}

/**
 * Gets the count of growers that have not been assigned to a branch
 */
export function getUnassignedGrowerCountForBranches(state: WizardState): number {
	const growerNames = getGrowerNames(state);
	const assignments = state.branchAssignments || {};
	return growerNames.filter((name) => !assignments[name]).length;
}

/**
 * Gets the count of growers that have not been assigned to a salesperson
 */
export function getUnassignedGrowerCountForSalespersons(state: WizardState): number {
	const growerNames = getGrowerNames(state);
	const assignments = state.salespersonAssignments || {};
	return growerNames.filter((name) => !assignments[name]).length;
}

/**
 * Checks if a step is a configuration step
 */
export function isConfigurationStep(step: WizardStep): step is ConfigurationStep {
	return CONFIGURATION_STEPS.includes(step as ConfigurationStep);
}

/**
 * Checks if a step is a validation step
 */
export function isValidationStep(step: WizardStep): step is ValidationStep {
	return VALIDATION_STEPS.includes(step as ValidationStep);
}

/**
 * Gets the number of issues for a given step
 */
export function getStepIssueCount(state: WizardState, step: WizardStep): number {
	// Configuration steps don't have "issues" in the same sense
	if (isConfigurationStep(step)) {
		return 0;
	}

	switch (step) {
		case "overlapping-boundaries":
			return state.issues.overlaps.length;
		case "overlapping-zones":
			return state.issues.overlappingZones.length;
		case "duplicate-boundaries":
			return state.issues.duplicateBoundaries.length;
		case "overlapping-points":
			return state.issues.overlappingPoints.length;
		case "unmatched-features":
			return state.issues.unmatched.length;
		case "branch-configuration":
			return getUnassignedGrowerCountForBranches(state);
		case "deal-configuration":
			return getUnassignedGrowerCount(state);
		case "salesperson-assignment":
			return getUnassignedGrowerCountForSalespersons(state);
		case "missing-invalid-data":
			return state.issues.invalidData.length;
		case "summary":
			return 0;
		default:
			return 0;
	}
}

/**
 * Gets the issues array for a given step
 */
export function getIssuesForStep(
	state: WizardState,
	step: WizardStep,
): (
	| OverlapIssue
	| DuplicateIssue
	| InternalDuplicateIssue
	| OverlappingZonesIssue
	| UnmatchedIssue
	| InvalidDataIssue
)[] {
	switch (step) {
		case "overlapping-boundaries":
			return state.issues.overlaps;
		case "overlapping-zones":
			return state.issues.overlappingZones;
		case "duplicate-boundaries":
			return state.issues.duplicateBoundaries;
		case "overlapping-points":
			return state.issues.overlappingPoints;
		case "unmatched-features":
			return state.issues.unmatched;
		case "branch-configuration":
			return []; // No issue-based pagination
		case "deal-configuration":
			return []; // No issue-based pagination
		case "salesperson-assignment":
			return []; // No issue-based pagination
		case "missing-invalid-data":
			return state.issues.invalidData;
		case "summary":
			return [];
		default:
			return [];
	}
}

/**
 * Gets the number of unskipped issues for the current step
 */
export function getUnskippedIssueCount(state: WizardState): number {
	const issues = getIssuesForStep(state, state.currentStep);
	return issues.filter((issue) => !state.skippedIssueIds.has(issue.id)).length;
}

/**
 * Determines if a step should be shown based on phase and whether it has issues
 * OR if it had issues initially (to show resolved state with checkmark)
 */
export function shouldShowStep(state: WizardState, step: WizardStep): boolean {
	// Configuration steps are always shown when in configuration phase
	// or when they have been completed (to show in progress bar)
	if (isConfigurationStep(step)) {
		// Always show in configuration phase
		if (state.phase === "configuration") {
			return true;
		}
		// Show if completed (user has progressed past it)
		if (state.completedSteps.has(step)) {
			return true;
		}
		// Don't show configuration steps in validation phase if not completed
		return false;
	}

	// Branch configuration only shows when company has branches (in validation phase)
	if (step === "branch-configuration") {
		return state.phase === "validation" && state.hasBranches;
	}

	// Summary, deal-configuration, and salesperson-assignment always show (in validation phase)
	if (step === "summary" || step === "deal-configuration" || step === "salesperson-assignment") {
		return state.phase === "validation";
	}

	// Validation steps - only show in validation phase
	if (state.phase !== "validation") {
		return false;
	}

	// Show if step currently has issues
	if (getStepIssueCount(state, step) > 0) {
		return true;
	}

	// Show if step had issues initially (even if now resolved)
	if (state.stepsWithInitialIssues.has(step)) {
		return true;
	}

	return false;
}

/**
 * Determines if a step is resolved (had issues initially, but now has no unskipped issues)
 * Used to show green checkmarks for steps that were fixed or had all issues skipped
 */
export function isStepResolved(state: WizardState, step: WizardStep): boolean {
	// Only steps that initially had issues can be "resolved"
	if (!state.stepsWithInitialIssues.has(step)) {
		return false;
	}

	// Check if current issue count is zero (resolved by fixing)
	const currentIssueCount = getStepIssueCount(state, step);
	if (currentIssueCount === 0) {
		return true;
	}

	// Check if all issues are skipped (resolved by skipping)
	const issues = getIssuesForStep(state, step);
	const allSkipped = issues.every((issue) => state.skippedIssueIds.has(issue.id));
	return allSkipped;
}

/**
 * Gets the first step that has issues (for navigation after validation)
 */
export function getFirstStepWithIssues(state: WizardState): WizardStep {
	for (const step of STEP_ORDER) {
		if (step === "summary") continue;
		if (getStepIssueCount(state, step) > 0) {
			return step;
		}
	}
	return "summary";
}
